/**
 * Daily horoscope (an extra the client asked for): twelve signs and a reading for each sign and day, picked from
 * short texts in every locale. The same sign gets the same reading all day, for everyone, on every device.
 */

export const ZODIAC_SIGNS = [
  'aries',
  'taurus',
  'gemini',
  'cancer',
  'leo',
  'virgo',
  'libra',
  'scorpio',
  'sagittarius',
  'capricorn',
  'aquarius',
  'pisces',
] as const;

export type ZodiacSign = (typeof ZODIAC_SIGNS)[number];

/** First day of each sign as [month, day], in ZODIAC_SIGNS order. */
const STARTS: readonly [number, number][] = [
  [3, 21],
  [4, 20],
  [5, 21],
  [6, 21],
  [7, 23],
  [8, 23],
  [9, 23],
  [10, 23],
  [11, 22],
  [12, 22],
  [1, 20],
  [2, 19],
];

/** How many texts each part of a reading has in the locales (keys horoscope.<part>.<n>). */
export const HOROSCOPE_TEXTS = { general: 10, love: 6, color: 6 } as const;

export interface HoroscopeReading {
  sign: ZodiacSign;
  /** Index into horoscope.general.* */
  general: number;
  /** Index into horoscope.love.* */
  love: number;
  /** Index into horoscope.color.* */
  color: number;
  luckyNumber: number;
}

/** The sign whose dates contain this month (1–12) and day. */
export function zodiacSignOf(month: number, day: number): ZodiacSign {
  const md = month * 100 + day;
  let found: ZodiacSign = 'capricorn';
  for (let i = 0; i < STARTS.length; i++) {
    const [m, d] = STARTS[i]!;
    const start = m * 100 + d;
    const next = STARTS[(i + 1) % STARTS.length]!;
    const end = next[0] * 100 + next[1];
    const inside = start < end ? md >= start && md < end : md >= start || md < end;
    if (inside) found = ZODIAC_SIGNS[i]!;
  }
  return found;
}

/** The sign's dates as [[month, day], [month, day]] (first and last day). */
export function zodiacDates(sign: ZodiacSign): [[number, number], [number, number]] {
  const i = ZODIAC_SIGNS.indexOf(sign);
  const [nm, nd] = STARTS[(i + 1) % STARTS.length]!;
  const last = new Date(Date.UTC(2001, nm - 1, nd - 1));
  return [STARTS[i]!, [last.getUTCMonth() + 1, last.getUTCDate()]];
}

/** FNV-1a: a small stable hash, so a sign and a day always give the same reading. */
function hash(text: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h;
}

/** Today's reading for a sign; `day` is the calendar date as YYYY-MM-DD. */
export function dailyHoroscope(sign: ZodiacSign, day: string): HoroscopeReading {
  const seed = (part: string) => hash(`${day}:${sign}:${part}`);
  return {
    sign,
    general: seed('general') % HOROSCOPE_TEXTS.general,
    love: seed('love') % HOROSCOPE_TEXTS.love,
    color: seed('color') % HOROSCOPE_TEXTS.color,
    luckyNumber: (seed('number') % 99) + 1,
  };
}
